'use client'

import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import { Box, Button, Stack } from '@mui/material'
import {
    OnboardingCapabilitiesSection,
    OnboardingHeader,
    OnboardingIntro,
    OnboardingTryAskingMobile,
    obVisual,
    type SRPFloatingAIOnboardingCopy
} from './SRPFloatingAIChatOnboardingShared'

export type SRPFloatingAIChatOnboardingDrawerProps = {
    open: boolean
    onboardingHeaderHeight: number
    onboardingCopy: SRPFloatingAIOnboardingCopy
    aiChatModeTitle: string
    onContinue: () => void
    onExit: () => void
}

export const SRPFloatingAIChatOnboardingDrawer = ({
    open,
    onboardingHeaderHeight,
    onboardingCopy,
    aiChatModeTitle,
    onContinue,
    onExit
}: SRPFloatingAIChatOnboardingDrawerProps) => (
    <SwipeableDrawerWithBackNavigation
        open={open}
        onClose={onContinue}
        disableSwipeToOpen
        isLoginFlow
        sx={{
            height: 'auto',
            maxHeight: '92vh',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            overflow: 'hidden',
            boxShadow: '0 -10px 40px rgba(15,27,61,0.25)'
        }}
    >
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <OnboardingHeader
                copy={onboardingCopy}
                aiChatModeTitle={aiChatModeTitle}
                compact
                flushTop
                onboardingHeaderHeight={onboardingHeaderHeight}
                onDismiss={onContinue}
            />
            <Box
                sx={{
                    position: 'absolute',
                    top: 10,
                    insetInline: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 1
                }}
            >
                <Box sx={{ width: 44, height: 5, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.45)' }} />
            </Box>
        </Box>
        <Box sx={{ overflowY: 'auto', px: 2, pt: 2, pb: 1, flex: 1, minHeight: 0 }}>
            <OnboardingIntro copy={onboardingCopy} />
            <OnboardingCapabilitiesSection copy={onboardingCopy} compact />
            <OnboardingTryAskingMobile copy={onboardingCopy} />
        </Box>
        <Box
            sx={{
                px: 2,
                pt: 1.5,
                pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                borderTop: '1px solid rgba(15,27,61,0.08)',
                bgcolor: '#fff',
                flexShrink: 0
            }}
        >
            <Stack direction="row" spacing={1}>
                <Button
                    fullWidth
                    onClick={onExit}
                    sx={{
                        color: 'text.secondary',
                        borderRadius: 2,
                        py: 1.25,
                        fontWeight: 600,
                        textTransform: 'none',
                        border: '1px solid rgba(15,27,61,0.12)'
                    }}
                >
                    {onboardingCopy.exit}
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={onContinue}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        py: 1.25,
                        fontWeight: 700,
                        bgcolor: obVisual.primary,
                        boxShadow: '0 8px 20px -8px rgba(30,58,95,0.5)',
                        '&:hover': { bgcolor: obVisual.primaryDark }
                    }}
                >
                    {onboardingCopy.continue}
                </Button>
            </Stack>
        </Box>
    </SwipeableDrawerWithBackNavigation>
)
